import {USER_CRITERIA_DEFAULT_VALUE} from '../configuration.constants';
/**
 * Setters owned by the Segmentation tab. Every method runs through the
 * controller's `setState(partial)` which patches the slice in the host
 * React component's state.
 *
 * Cross-tab effect: editing the segmentation policy can invalidate
 * available-balance rules. Each setter computes the post-update policy
 * locally and passes it as explicit arguments to `notifyAvailableBalance`
 * so the sanitization happens in the same React batch without needing to
 * read in-flight sibling state.
 */
class SegmentationDelegate {
    toggleRestrictedCriterion(key) {
        this.setState((prev) => {
            const current = prev.segment_policy?.restrictedCriteria?.[key] || {};

            const next = {
                ...USER_CRITERIA_DEFAULT_VALUE[key], // Start with defaults
                ...current,       // Override with current values
                enabled: !current.enabled  // Toggle enabled state
            };

            const restrictedCriteria = { ...prev.segment_policy.restrictedCriteria, [key]: next };
            return { segment_policy: { ...prev.segment_policy, restrictedCriteria } };
        });
    }

    setRestrictedCriterionOperator(key, operator) {
        this.setState((prev) => {
            const current = prev.segment_policy?.restrictedCriteria?.[key] || {};
            const next = { ...current, operator };
            const restrictedCriteria = { ...prev.segment_policy.restrictedCriteria, [key]: next };
            return { segment_policy: { ...prev.segment_policy, restrictedCriteria } };
        });
    }

    setRestrictedCriterionValue(key, value) {
        this.setState((prev) => {
            const current = prev.segment_policy?.restrictedCriteria?.[key] || {};
            const next = { ...current, value };
            const restrictedCriteria = { ...prev.segment_policy.restrictedCriteria, [key]: next };
            return { segment_policy: { ...prev.segment_policy, restrictedCriteria } };
        });
    }

    setRestrictedCriterionWindow(key, window_days) {
        this.setState((prev) => {
            const current = prev.segment_policy?.restrictedCriteria?.[key] || {};
            const next = { ...current, window_days };
            const restrictedCriteria = { ...prev.segment_policy.restrictedCriteria, [key]: next };
            return { segment_policy: { ...prev.segment_policy, restrictedCriteria } };
        });
    }

    setSegmentationMode(mode) {
        // 'blocked' is a special UI mode that maps to 'restricted' for storage
        const actualMode = mode === 'blocked' ? 'restricted' : mode;

        this.setState((prev) => ({
            segment_policy: { ...prev.segment_policy, mode: actualMode }
        }));
        this.notifyAvailableBalance(
            { ...this.state.segment_policy, mode: actualMode },
            null
        );
    }

    setBlockOutOfSegmentation(blockOutOfSegmentation) {
        this.setState((prev) => ({
            segment_policy: { ...prev.segment_policy, blockOutOfSegmentation }
        }));
    }

    addToWhitelist(memberId) {
        const trimmed = typeof memberId === 'string' ? memberId.trim() : '';
        if (!trimmed) return;

        this.setState((prev) => {
            const current = prev.whitelistRules || [];
            if (current.some(m => m.member_id === trimmed)) return null;
            return {
                whitelistRules: [...current, { member_id: trimmed, action: 'ALLOW' }]
            };
        });
    }

    removeFromWhitelist(memberId, ruleId) {
        this.setState((prev) => ({
            whitelistRules: (prev.whitelistRules || []).filter(
                (member) => member.id !== ruleId
            )
        }));
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Blocked Members (DENY overrides) — Client Access List handlers
    // Makes direct API calls to POST/DELETE access rules
    // ─────────────────────────────────────────────────────────────────────────

    addBlockedMember(memberId) {
        if (!memberId) return;

        this.setState((prev) => {
            const current = prev.blockedMembers || [];
            if (current.some(m => m.member_id === memberId)) return null;
            return {
                blockedMembers: [...current, { member_id: memberId, action: 'DENY' }]
            };
        });
    }

    removeBlockedMember(memberId, ruleId) {
        this.setState((prev) => ({
            blockedMembers: (prev.blockedMembers || []).filter(
                (member) => member.id !== ruleId
            )
        }));
    }

    setSegmentPolicy(segmentPolicy) {
        this.setState({ segment_policy: segmentPolicy });
        this.notifyAvailableBalance(segmentPolicy, null);
    }
}

export default SegmentationDelegate;
